import React, { useState, useEffect, useRef, useContext } from "react";
import Button from "../../components/comman/button.js";
import LoadingSpinner from "../../components/LoadingSpinner/loadingSpinner.js";
import ErrorDisplay from "../../components/comman/ErrorDisplay.js";
import Toast from "../../components/Toast/toast.js";
import { getTranslation } from "../../utils/translate.js";
import { useLanguage } from "../../utils/languageContext.js";
import companyService from "../../services/company-service.js";

import Modal from "../../components/comman/modal.js";
import MapDialog from "../../components/Map/MapDialog.js";
import AddressSearchContextProvider, { AddressSearchContext } from "../../context/addressSearchContext.js";
import { YMAP_KEY } from "../../constants/index.js";

/**
 * Fallback parser for an address line like "Armenia, Yerevan, Moskovyan Street, 8".
 */
const parseAddressLine = (addressLine = '') => {
    const parts = addressLine.split(',').map(part => part.trim()).filter(Boolean);
    // Drop the country (first part) when there is more than just a city left
    const rest = parts.length > 1 ? parts.slice(1) : parts;
    const city = rest[0] || '';
    const last = rest[rest.length - 1] || '';
    const hasHome = rest.length >= 3 && /^\d/.test(last);
    const street = rest.slice(1, hasHome ? -1 : undefined).join(', ');
    return { city, street, home: hasHome ? last : '' };
};

/**
 * Reverse-geocodes [lat, lng] with Yandex and returns { city, street, home }.
 */
const reverseGeocode = async ([lat, lng], language) => {
    const url = new URL("https://geocode-maps.yandex.ru/1.x");
    url.searchParams.set("apikey", YMAP_KEY);
    url.searchParams.set("geocode", `${lng},${lat}`); // Yandex expects "lng,lat"
    url.searchParams.set("kind", "house");
    url.searchParams.set("results", "1");
    url.searchParams.set("lang", `${language === 'am' ? 'hy' : language}_AM`);
    url.searchParams.set("format", "json");

    const res = await fetch(url);
    if (!res.ok) throw new Error(`Yandex API error: ${res.status}`);
    const data = await res.json();
    const components = data.response?.GeoObjectCollection?.featureMember?.[0]
        ?.GeoObject?.metaDataProperty?.GeocoderMetaData?.Address?.Components;
    if (!Array.isArray(components)) throw new Error("No address components");

    const find = (...kinds) => components.find(c => kinds.includes(c.kind))?.name || '';
    return {
        city: find('locality') || find('area', 'province'),
        street: find('street', 'district'),
        home: find('house'),
    };
};

/**
 * Opens the Yandex map dialog; on confirm, passes the picked location to onApply
 * as { coordinates: [lat, lng], city, street, home }.
 * Must be rendered inside AddressSearchContextProvider.
 */
const CompanyLocationPicker = ({ open, onOpenChange, initialCoordinates, onApply }) => {
    const { language } = useLanguage();
    const { selectedAddress } = useContext(AddressSearchContext);

    const handleApplyAddress = async (callback) => {
        const location = selectedAddress?.location;
        if (Array.isArray(location) && location.length >= 2) {
            const coordinates = [Number(location[0]), Number(location[1])];
            let addressParts;
            try {
                addressParts = await reverseGeocode(coordinates, language);
            } catch (err) {
                addressParts = parseAddressLine(selectedAddress.address);
            }
            onApply({ coordinates, ...addressParts });
        }
        if (callback) callback();
    };

    return (
        <MapDialog
            open={open}
            onOpenChange={onOpenChange}
            onApplyAddress={handleApplyAddress}
            initialCoordinates={initialCoordinates}
        />
    );
};

/**
 * Backend stores address coordinates as [latitude, longitude].
 * Returns { latitude, longitude } or null when coordinates are missing/invalid.
 */
const getLatLng = (address) => {
    const coordinates = address?.coordinates;
    if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
    const latitude = parseFloat(coordinates[0]);
    const longitude = parseFloat(coordinates[1]);
    if (isNaN(latitude) || isNaN(longitude)) return null;
    return { latitude, longitude };
};

const CompanyManagement = () => {
    const { language } = useLanguage();
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [currentCompany, setCurrentCompany] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [companyToDelete, setCompanyToDelete] = useState(null);
    const [latitude, setLatitude] = useState('');
    const [longitude, setLongitude] = useState('');
    const [city, setCity] = useState('');
    const [street, setStreet] = useState('');
    const [home, setHome] = useState('');
    const [isMapOpen, setIsMapOpen] = useState(false);
    const toastRef = useRef(null);

    // Sync map-driven inputs with the company being edited
    useEffect(() => {
        if (!isEditing) return;
        const address = currentCompany?.address;
        const latLng = getLatLng(address);
        setLatitude(latLng ? String(latLng.latitude) : '');
        setLongitude(latLng ? String(latLng.longitude) : '');
        setCity(address?.city || '');
        setStreet(address?.street || '');
        setHome(address?.home || '');
    }, [isEditing, currentCompany]);

    const handleMapApply = ({ coordinates: [lat, lng], city, street, home }) => {
        setLatitude(lat.toFixed(6));
        setLongitude(lng.toFixed(6));
        setCity(city);
        setStreet(street);
        setHome(home);
    };

    const fetchCompanies = async () => {
        setLoading(true);
        try {
            const data = await companyService.getCompanies();
            setCompanies(data || []);
            setError(null);
        } catch (err) {
            console.error("Error fetching companies:", err);
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCompanies();
    }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);

        const latRaw = (formData.get('latitude') || '').toString().trim();
        const lngRaw = (formData.get('longitude') || '').toString().trim();
        const lat = latRaw === '' ? null : parseFloat(latRaw);
        const lng = lngRaw === '' ? null : parseFloat(lngRaw);

        // Both coordinates must be provided together and be valid numbers
        const hasAnyCoordinate = lat !== null || lng !== null;
        if (hasAnyCoordinate && (lat === null || lng === null || isNaN(lat) || isNaN(lng))) {
            toastRef.current?.addToast(
                getTranslation('clients.invalidCoordinates', language) || "Invalid coordinates",
                "error"
            );
            return;
        }

        const companyData = {
            name: formData.get('name'),
            address: {
                country: formData.get('country') || null,
                city: formData.get('city') || null,
                street: formData.get('street') || null,
                home: formData.get('home') || null,
                floor: formData.get('floor') || null,
                entrance: formData.get('entrance') || null,
                flat: formData.get('flat') || null,
                // Backend expects [lat, lng] (latitude first, then longitude)
                coordinates: hasAnyCoordinate ? [lat, lng] : null,
            }
        };

        try {
            if (currentCompany) {
                await companyService.updateCompany(currentCompany.id, companyData);
                toastRef.current?.addToast(getTranslation('common.success', language) || "Success", "success");
            } else {
                await companyService.createCompany(companyData);
                toastRef.current?.addToast(getTranslation('common.success', language) || "Success", "success");
            }
            setIsEditing(false);
            setCurrentCompany(null);
            fetchCompanies();
        } catch (err) {
            console.error("Error saving company:", err);
            toastRef.current?.addToast(getTranslation('common.error', language) || "Error", "error");
        }
    };

    const handleDeleteClick = (company) => {
        setCompanyToDelete(company);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!companyToDelete) return;
        try {
            await companyService.deleteCompany(companyToDelete.id);
            toastRef.current?.addToast(getTranslation('common.success', language) || "Success", "success");
            setIsDeleteModalOpen(false);
            setCompanyToDelete(null);
            fetchCompanies();
        } catch (err) {
            console.error("Error deleting company:", err);
            toastRef.current?.addToast(getTranslation('common.error', language) || "Error", "error");
        }
    };

    if (isEditing) {
        const lat = parseFloat(latitude);
        const lng = parseFloat(longitude);
        const mapInitialCoordinates = !isNaN(lat) && !isNaN(lng) ? [lat, lng] : null;
        return (
            <div className="bg-white dark:bg-[#161b22] rounded-lg shadow-sm p-4 border border-gray-100 dark:border-[#30363d]">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-[#c9d1d9]">
                        {currentCompany ? 
                            (getTranslation('clients.editCompany', language) || "Edit Company") : 
                            (getTranslation('clients.addCompany', language) || "Add Company")}
                    </h2>
                    <Button variant="ghost" onClick={() => { setIsEditing(false); setCurrentCompany(null); }}>
                        {getTranslation('common.cancel', language) || "Cancel"}
                    </Button>
                </div>
                <form onSubmit={handleSave} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-[#c9d1d9] mb-1">
                            {getTranslation('clients.companyName', language) || "Company Name"}
                        </label>
                        <input name="name" defaultValue={currentCompany?.name} required className="w-full px-3 py-2 border rounded-lg dark:bg-[#0d1117] dark:border-[#30363d] dark:text-[#c9d1d9]" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-[#c9d1d9] mb-1">
                                {getTranslation('orderContentPage.country', language) || "Country"}
                            </label>
                            <input name="country" defaultValue={currentCompany?.address?.country} className="w-full px-3 py-2 border rounded-lg dark:bg-[#0d1117] dark:border-[#30363d] dark:text-[#c9d1d9]" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-[#c9d1d9] mb-1">
                                {getTranslation('orderContentPage.city', language) || "City"}
                            </label>
                            <input name="city" value={city} onChange={(e) => setCity(e.target.value)}className="w-full px-3 py-2 border rounded-lg dark:bg-[#0d1117] dark:border-[#30363d] dark:text-[#c9d1d9]" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-[#c9d1d9] mb-1">
                                {getTranslation('orderContentPage.street', language) || "Street"}
                            </label>
                            <input name="street" value={street} onChange={(e) => setStreet(e.target.value)}className="w-full px-3 py-2 border rounded-lg dark:bg-[#0d1117] dark:border-[#30363d] dark:text-[#c9d1d9]" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-[#c9d1d9] mb-1">
                                {getTranslation('orderContentPage.home', language) || "Home"}
                            </label>
                            <input name="home" value={home} onChange={(e) => setHome(e.target.value)}className="w-full px-3 py-2 border rounded-lg dark:bg-[#0d1117] dark:border-[#30363d] dark:text-[#c9d1d9]" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-[#c9d1d9] mb-1">
                                {getTranslation('clients.longitude', language) || "Longitude"}
                            </label>
                            <input
                                name="longitude"
                                type="number"
                                step="any"
                                min="-180"
                                max="180"
                                placeholder="44.477386"
                                value={longitude}
                                onChange={(e) => setLongitude(e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg dark:bg-[#0d1117] dark:border-[#30363d] dark:text-[#c9d1d9]"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-[#c9d1d9] mb-1">
                                {getTranslation('clients.latitude', language) || "Latitude"}
                            </label>
                            <input
                                name="latitude"
                                type="number"
                                step="any"
                                min="-90"
                                max="90"
                                placeholder="40.188976"
                                value={latitude}
                                onChange={(e) => setLatitude(e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg dark:bg-[#0d1117] dark:border-[#30363d] dark:text-[#c9d1d9]"
                            />
                        </div>
                        <div className="col-span-2">
                            <Button variant="secondary" size="sm" onClick={() => setIsMapOpen(true)}>
                                <span className="inline-flex items-center gap-2">
                                    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                        <circle cx="12" cy="10" r="3"></circle>
                                    </svg>
                                    {getTranslation('profile.selectOnMap', language) || "Select on map"}
                                </span>
                            </Button>
                        </div>
                    </div>
                    <Button variant="primary" type="submit">
                        {getTranslation('common.save', language) || "Save"}
                    </Button>
                </form>
                {isMapOpen && (
                    <AddressSearchContextProvider>
                        <CompanyLocationPicker
                            open={isMapOpen}
                            onOpenChange={setIsMapOpen}
                            initialCoordinates={mapInitialCoordinates}
                            onApply={handleMapApply}
                        />
                    </AddressSearchContextProvider>
                )}
                <Toast ref={toastRef} />
            </div>
        );
    }

    if (loading) return <LoadingSpinner />;
    if (error) return <ErrorDisplay error={error} onRetry={fetchCompanies} />;

    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-gray-900 dark:text-[#c9d1d9]">
                    {getTranslation('clients.companies', language) || 'Companies'}
                </h2>
                <Button variant="primary" onClick={() => setIsEditing(true)}>
                    + {getTranslation('clients.addCompany', language) || "Add Company"}
                </Button>
            </div>
            
            {companies.length === 0 ? (
                <div className="text-center py-8 bg-white dark:bg-[#161b22] rounded-lg border dark:border-[#30363d]">
                    <p className="text-gray-500 dark:text-[#8b949e]">
                        {getTranslation('clients.noCompanies', language) || "No companies found"}
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {companies.map(company => {
                        const latLng = getLatLng(company.address);
                        return (
                        <div key={company.id} className="bg-white dark:bg-[#161b22] p-4 rounded-lg border border-gray-200 dark:border-[#30363d] shadow-sm">
                            <h3 className="font-bold text-lg text-gray-900 dark:text-[#c9d1d9]">{company.name}</h3>
                            <p className="text-sm text-gray-500 dark:text-[#8b949e] mt-1">
                                {[company.address?.city, company.address?.street, company.address?.home].filter(Boolean).join(", ")}
                            </p>
                            {latLng && (
                                <p className="text-xs text-gray-400 dark:text-[#6e7681] mt-1">
                                    {getTranslation('clients.longShort', language) || "long"}: {latLng.longitude.toFixed(6)}, {getTranslation('clients.latShort', language) || "lat"}: {latLng.latitude.toFixed(6)}
                                </p>
                            )}
                            <div className="mt-4 flex gap-2">
                                <Button variant="secondary" size="sm" onClick={() => { setCurrentCompany(company); setIsEditing(true); }}>
                                    {getTranslation('common.edit', language) || "Edit"}
                                </Button>
                                <Button variant="danger" size="sm" onClick={() => handleDeleteClick(company)}>
                                    {getTranslation('common.delete', language) || "Delete"}
                                </Button>
                            </div>
                        </div>
                        );
                    })}
                </div>
            )}

            <Modal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                title={getTranslation('common.areYouSure', language) || "Are you sure?"}
                size="sm"
            >
                <div className="p-1 text-gray-600 dark:text-[#8b949e]">
                    <p className="mb-6">{getTranslation('common.confirmDelete', language) || "Are you sure you want to delete?"}</p>
                    <div className="flex justify-end gap-3 mt-4">
                        <Button variant="ghost" onClick={() => setIsDeleteModalOpen(false)}>
                            {getTranslation('common.cancel', language) || "Cancel"}
                        </Button>
                        <Button variant="danger" onClick={handleDeleteConfirm}>
                            {getTranslation('common.delete', language) || "Delete"}
                        </Button>
                    </div>
                </div>
            </Modal>

            <Toast ref={toastRef} />
        </div>
    );
};

export default CompanyManagement;
